import { ClipboardCheck } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';

import { TotemLightBox } from 'components/TotemLightBox';
import { colors } from 'constants/colors';

export const TaskIcon = ({ size = 16, color = colors.pink }: { size?: number; color?: string }) => (
    <ClipboardCheck size={size} color={color} strokeWidth={2.5} className="shrink-0" aria-label="Tâche" />
);

export const TaskPhotos = ({ photos }: { photos: { url: string }[] }) => {
    const [openIndex, setOpenIndex] = useState<number | null>(null);
    const images = useMemo(() => photos.map(({ url }) => ({ url })), [photos]);

    // Escape closes the viewer only: the task window's dialog listens on `document`, a window capture runs first.
    useEffect(() => {
        if (openIndex == null) return;
        const closeViewer = (event: KeyboardEvent): void => {
            if (event.key !== 'Escape') return;
            event.stopPropagation();
            setOpenIndex(null);
        };
        window.addEventListener('keydown', closeViewer, true);
        return () => window.removeEventListener('keydown', closeViewer, true);
    }, [openIndex]);

    return (
        <>
            <Photos>
                {photos.map(({ url }, index) => (
                    <img key={url} src={url} alt="Photo de la tâche" onClick={() => setOpenIndex(index)} />
                ))}
            </Photos>
            {openIndex != null &&
                createPortal(
                    <LightboxLayer>
                        <TotemLightBox
                            images={images}
                            initialImageIndex={openIndex}
                            isOpen
                            setIsOpen={() => setOpenIndex(null)}
                        />
                    </LightboxLayer>,
                    document.body,
                )}
        </>
    );
};

// Above the task window and the sticky table cells, which a lightbox drawn in place stays under.
const LightboxLayer = styled.div`
    position: relative;
    z-index: 1000;
    pointer-events: auto;
`;

const Photos = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 6px;

    img {
        width: 96px;
        height: 96px;
        object-fit: cover;
        border-radius: 6px;
        border: 1px solid ${({ theme }) => theme.lightBorderColor};
        cursor: zoom-in;
    }
`;
